import React, {useEffect, useState} from 'react';
import {Form as FormAntd, Modal} from 'antd';
import {WarningOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {ModalLvl} from '../../../types/settings';
import {LinkBack} from '../../../ui-kit/Button/LinkBack';
import {
  GroupTitle,
  MessageFormWrapper,
  LeftSide,
  RightSide,
  CodeFormItem,
  FormButton,
  ErrorLine,
  ErrorText,
  CodeForm,
  PageWrapper,
  TitleLine,
  ModalDescr,
  SuccessLine,
  SuccessText,
} from './styles';
import Input, {TextArea} from '../../../ui-kit/Input';
import {FormMode, TemplateCreateFields} from '../../../types/messages';
import {useCreateTemplateT} from '../../../hooks/templates';
import {Loader} from '../../../ui-kit/Loader';
import {templateBody} from '../../../queries/types';
import {route} from '../../../constants/routes';
import {ItemLabel} from '../../../ui-kit/Labels';
import {InstructionForVariables} from '../../../containers/Template/InstructionForVariables';
import {FormTips} from '../../Message/common';
import {StyledSpan, TextLimitTip} from './styles';

export type TemplateFormProps = {
  formData: useCreateTemplateT;
  mode: FormMode;
  initial?: Partial<templateBody>;
  isLoading?: boolean;
};

const formTitles = {create: 'Create New Message Template', edit: 'Edit Message Template'};
const formButtons = {create: 'Save Template', edit: 'Save Template'};

export const TemplateForm: React.FC<TemplateFormProps> = ({formData, mode, initial, isLoading}) => {
  const {onCreate, error, success, resetResult} = formData;
  const [form] = FormAntd.useForm();
  const title = formTitles[mode];
  const button = formButtons[mode];
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const goManageTemplates = () => navigate(route.manageTemplates.path);

  const [delModalLvl, setDelModalLvl] = useState<ModalLvl>(ModalLvl.closed);
  const setDelPopup = (lvl: ModalLvl) => setDelModalLvl(lvl);
  const [loading, setLoading] = useState(false);
  const [smsText, setSmsText] = useState<{content: string; limit: number}>({content: '', limit: 160});
  const [emailText, setEmailText] = useState<{content: string; limit: number}>({content: '', limit: 2000});
  const [activeMessageField, setActiveMessageField] = useState<string | null>(null);

  useEffect(() => {
    if (delModalLvl === ModalLvl.confirm) {
      Modal.confirm({
        title: `Are you sure you want to leave Create new Message?`,
        content: <ModalDescr>You cannot undo this action.</ModalDescr>,
        okText: 'Yes',
        cancelText: 'No',
        autoFocusButton: null,
        icon: <WarningOutlined />,
        onOk: () => setDelPopup(ModalLvl.success),
        onCancel: () => setDelPopup(ModalLvl.closed),
      });
    }
    if (delModalLvl === ModalLvl.success) {
      Modal.success({
        title: ``,
        content: <ModalDescr></ModalDescr>,
        okText: 'Done',
        onOk: () => setDelPopup(ModalLvl.closed),
      });
    }
  }, [delModalLvl]);

  useEffect(() => {
    if (success?.status === 'completed') goManageTemplates();
  }, [success]);

  useEffect(() => {
    if (error) {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    if (initial) {
      // Initialize from API data with backward compatibility
      if (initial?.smsMessage !== undefined) {
        form.setFieldsValue({[TemplateCreateFields.smsMessage]: initial.smsMessage});
        setSmsText((prev) => ({...prev, content: initial.smsMessage || ''}));
      } else if (initial?.message) {
        // Backward compatibility: use message for SMS if smsMessage not available
        form.setFieldsValue({[TemplateCreateFields.smsMessage]: initial.message});
        setSmsText((prev) => ({...prev, content: initial.message || ''}));
      }
      if (initial?.emailMessage !== undefined) {
        form.setFieldsValue({[TemplateCreateFields.emailMessage]: initial.emailMessage});
        setEmailText((prev) => ({...prev, content: initial.emailMessage || ''}));
      } else if (initial?.message) {
        // Backward compatibility: use message for Email if emailMessage not available
        form.setFieldsValue({[TemplateCreateFields.emailMessage]: initial.message});
        setEmailText((prev) => ({...prev, content: initial.message || ''}));
      }
      // Keep message field for backward compatibility
      form.setFieldsValue({[TemplateCreateFields.message]: initial.message});
    }
  }, [initial, form]);

  const handleChangeForm = () => {
    setLoading(false);
    resetResult?.();
  };

  const onFinish = (values: any) => {
    onCreate?.({...values});
    setLoading(true);
  };

  const inputsFocusing = (e: any) => {
    const value = e?.target?.value || '';
    const fieldName = e?.target?.id;
    setActiveMessageField(fieldName);
    if (fieldName === TemplateCreateFields.smsMessage) {
      setSmsText((prev) => ({...prev, content: value}));
      form.setFieldsValue({[TemplateCreateFields.smsMessage]: value});
    } else if (fieldName === TemplateCreateFields.emailMessage) {
      setEmailText((prev) => ({...prev, content: value}));
      form.setFieldsValue({[TemplateCreateFields.emailMessage]: value});
    } else if (fieldName === TemplateCreateFields.message) {
      form.setFieldsValue({[TemplateCreateFields.message]: value});
    }
  };

  const onSelectDynamicTemplate = (value: string) => () => {
    if (activeMessageField) {
      const current = form.getFieldValue(activeMessageField) || '';
      form.setFieldsValue({[activeMessageField]: current + value});
      if (activeMessageField === TemplateCreateFields.smsMessage) {
        setSmsText((prev) => ({...prev, content: current + value}));
      } else if (activeMessageField === TemplateCreateFields.emailMessage) {
        setEmailText((prev) => ({...prev, content: current + value}));
      }
    } else {
      // Default to emailMessage if no field is focused
      const current = form.getFieldValue(TemplateCreateFields.emailMessage) || '';
      form.setFieldsValue({[TemplateCreateFields.emailMessage]: current + value});
      setEmailText((prev) => ({...prev, content: current + value}));
    }
  };

  if (isLoading)
    return (
      <PageWrapper>
        <Loader />
      </PageWrapper>
    );

  return (
    <PageWrapper>
      <TitleLine>
        <LinkBack onClick={goBack}>Back to Manage Templates</LinkBack>
      </TitleLine>
      <CodeForm layout={'vertical'} form={form} onChange={handleChangeForm} onFinish={onFinish}>
        <GroupTitle>{title}</GroupTitle>
        <MessageFormWrapper>
          <LeftSide>
            <CodeFormItem
              label={<ItemLabel label="Message Title*" />}
              rules={[{required: true, message: 'Message Title is required'}]}
              name={[TemplateCreateFields.name]}
              initialValue={initial?.name}>
              <Input />
            </CodeFormItem>
            <CodeFormItem
              label={<ItemLabel label="Email Message Body*" title={FormTips.emailBody} />}
              rules={[{required: true, message: 'Email Message Body is required', max: 2000, type: 'string'}]}
              name={[TemplateCreateFields.emailMessage]}>
              <StyledSpan key="note">2000 or fewer characters. Supports HTML as well.</StyledSpan>
              <TextArea
                value={emailText.content || ''}
                rows={8}
                id={TemplateCreateFields.emailMessage}
                maxLength={2000}
                placeholder={'Type or paste your email message...'}
                onChange={inputsFocusing}
                onFocus={inputsFocusing}
              />
              <TextLimitTip key="tip" len={emailText.content.length || 0} max={2000}>
                {`${emailText.content.length || 0}/2000`}
              </TextLimitTip>
            </CodeFormItem>
            <CodeFormItem
              label={<ItemLabel label="SMS Message Body*" title={FormTips.smsBody} />}
              rules={[{required: true, message: 'SMS Message Body is required', max: 160, type: 'string'}]}
              name={[TemplateCreateFields.smsMessage]}>
              <StyledSpan key="note">
                Recommended 160 or fewer characters. Plain text only. Avoid emojis or special characters.
              </StyledSpan>
              <TextArea
                value={smsText.content || ''}
                rows={8}
                id={TemplateCreateFields.smsMessage}
                maxLength={160}
                placeholder={'Type or paste your sms message...'}
                onChange={inputsFocusing}
                onFocus={inputsFocusing}
              />
              <TextLimitTip key="tip" len={smsText.content.length || 0} max={160}>
                {`${smsText.content.length || 0}/160`}
              </TextLimitTip>
            </CodeFormItem>
            {/* Hidden field for backward compatibility */}
            <CodeFormItem name={[TemplateCreateFields.message]} style={{display: 'none'}}>
              <Input />
            </CodeFormItem>
          </LeftSide>
          <RightSide>
            <InstructionForVariables onSelect={onSelectDynamicTemplate} />
            <ErrorLine>{error && <ErrorText>{error}</ErrorText>}</ErrorLine>
            {success?.status === 'completed' ? (
              <Success mode={mode} textName="Message Template" />
            ) : (
              <FormButton htmlType="submit" type={'primary'} disabled={loading}>
                {button}
              </FormButton>
            )}
          </RightSide>
        </MessageFormWrapper>
      </CodeForm>
    </PageWrapper>
  );
};

export const Success: React.FC<{mode: FormMode; textName: string}> = ({mode, textName}) => {
  return (
    <SuccessLine>
      {mode === 'create' ? (
        <SuccessText>{textName} successfully created</SuccessText>
      ) : (
        <SuccessText>{textName} successfully updated</SuccessText>
      )}
    </SuccessLine>
  );
};
